import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatProps,
  StatNumberProps,
  StatLabelProps,
  Skeleton,
  SkeletonProps,
} from "@chakra-ui/react";
import { NA } from "components/Shared/EmptyPlaceholder";

type IsLoadedParams = {
  /** Force skeleton to show (false) or hide (true), overriding all other
   * loading logic */
  isLoaded: boolean | undefined;
  /** Loading state from React context - used as fallback when direct props not
   * provided */
  isLoadedContext: boolean | undefined;
  /** While true, skeleton shows unless value is present - use for async data
   * fetching */
  isLoading: boolean | undefined;
  /** Loading state from React context - same logic as isLoading but lower
   * priority */
  isLoadingContext: boolean | undefined;
  /** Force this specific value to be considered loaded regardless of presence
   * */
  isValueLoaded: boolean | undefined;
  /** Explicitly control whether value is valid - when false, shows placeholder
   * instead */
  isValuePresent: boolean | undefined;
  /** The actual value being rendered - used to infer presence if isValuePresent
   * not provided */
  value: ReactNode;
};

export type IsLoadedProps = Omit<
  Partial<IsLoadedParams>,
  "isLoadedContext" | "isLoadingContext" | "isValuePresent" | "value"
>;

export function getIsLoaded({
  isLoaded: isLoadedProp,
  isLoadedContext,
  isLoading: isLoadingProp,
  isLoadingContext,
  isValueLoaded: isValueLoadedProp,
  isValuePresent,
  value,
}: IsLoadedParams) {
  if (typeof isLoadedProp === "boolean") {
    return isLoadedProp;
  }

  const isValueLoaded = (() => {
    if (typeof isValueLoadedProp === "boolean") return isValueLoadedProp;
    if (typeof isValuePresent === "boolean") return isValuePresent;
    return value !== undefined;
  })();

  if (typeof isLoadingProp === "boolean") {
    return isValueLoaded || !isLoadingProp;
  }

  if (typeof isLoadedContext === "boolean") {
    return isLoadedContext;
  }

  if (typeof isLoadingContext === "boolean") {
    return isValueLoaded || !isLoadingContext;
  }

  return true;
}

type DisplayedValueParams = {
  /** Override automatic presence detection - use when value is complex
   * (components) or when primitive falsy values should be considered present */
  isValuePresent: boolean | undefined;
  /** Content to display - automatically considered empty if
   * null/undefined/empty string */
  value: ReactNode;
  /** What to show when value is empty - defaults to <NA /> component */
  valuePlaceholder: ReactNode | undefined;
};

export type DisplayedValueProps = Partial<DisplayedValueParams> & {
  value: ReactNode;
};

export function getDisplayedValue({
  isValuePresent: isValuePresentProp,
  value,
  valuePlaceholder = <NA />,
}: DisplayedValueParams) {
  const isValuePresent =
    typeof isValuePresentProp === "boolean"
      ? isValuePresentProp
      : value !== null && value !== undefined && value !== "";

  if (isValuePresent) return value ?? valuePlaceholder;

  return valuePlaceholder;
}

export type LabeledValueLoadingContext = {
  /** Force all child components to show as ready (true) or show skeletons
   * (false) */
  isLoaded?: boolean;
  /** Show skeletons for child components unless they have values present */
  isLoading?: boolean;
};

export type LabeledValueContextValue = LabeledValueLoadingContext & {
  labelProps?: StatLabelProps;
  statProps?: StatProps;
  valueProps?: StatNumberProps;
};

export const labeledValueContext = createContext<LabeledValueContextValue>({
  isLoading: false,
});

export const labelStyles = {
  color: "magenta.dark",
  textTransform: "uppercase",
  fontSize: ".8em",
  fontWeight: 700,
} as const;

export type LabeledValueProps = StatProps &
  IsLoadedProps &
  DisplayedValueProps & {
    below?: ReactNode;
    hasSecondaryValue?: boolean;
    helpText?: ReactNode;
    isLarge?: boolean;
    label: ReactNode;
    labelProps?: StatLabelProps;
    secondaryValue?: ReactNode;
    secondaryValueProps?: SkeletonProps;
    valueProps?: StatNumberProps;
  };

export default function LabeledValue({
  below,
  helpText,
  isLarge = false,
  isLoaded: isLoadedProp,
  isLoading: isLoadingProp,
  isValueLoaded,
  isValuePresent,
  label,
  labelProps,
  secondaryValue,
  secondaryValueProps,
  hasSecondaryValue = !!secondaryValue,
  value,
  valuePlaceholder,
  valueProps,
  ...rest
}: LabeledValueProps) {
  const {
    isLoaded: isLoadedContext,
    isLoading: isLoadingContext,
    labelProps: labelPropsContext,
    statProps: statPropsContext,
    valueProps: valuePropsContext,
  } = useContext(labeledValueContext);

  const isLoaded = getIsLoaded({
    isLoaded: isLoadedProp,
    isLoadedContext,
    isLoading: isLoadingProp,
    isLoadingContext,
    isValueLoaded,
    isValuePresent,
    value,
  });

  return (
    <Stat
      py={3}
      variant="labeledValue"
      data-large={isLarge || undefined}
      {...statPropsContext}
      {...rest}
    >
      <StatLabel {...labelPropsContext} {...labelProps}>
        {label}
      </StatLabel>

      <StatNumber
        display="flex"
        flexFlow="row"
        alignItems="baseline"
        {...valuePropsContext}
        {...valueProps}
      >
        <Skeleton isLoaded={isLoaded} display="inline-block" maxW="100%">
          {getDisplayedValue({
            isValuePresent,
            value,
            valuePlaceholder,
          })}
        </Skeleton>

        {hasSecondaryValue && (
          <Skeleton
            as="small"
            isLoaded={isLoaded}
            display="inline-block"
            fontWeight="light"
            ml={2}
            fontSize=".65em"
            {...secondaryValueProps}
          >
            {secondaryValue ?? <NA />}
          </Skeleton>
        )}
      </StatNumber>

      {helpText && <StatHelpText>{helpText}</StatHelpText>}

      {below}
    </Stat>
  );
}

export type LabeledValueContextProviderProps = LabeledValueContextValue & {
  children: ReactNode;
};

export function LabeledValueContextProvider({
  children,
  isLoaded,
  isLoading,
  labelProps,
  statProps,
  valueProps,
}: LabeledValueContextProviderProps) {
  const value = useMemo(
    () => ({ isLoaded, isLoading, labelProps, statProps, valueProps }),
    [isLoaded, isLoading, labelProps, statProps, valueProps],
  );
  return (
    <labeledValueContext.Provider value={value}>
      {children}
    </labeledValueContext.Provider>
  );
}
